import { useTheme, useMediaQuery, Breakpoint } from '@mui/material';
import { AppTheme } from '../themeConfig';
import { useMemo, useCallback } from 'react';

type Query = 'up' | 'down' | 'between' | 'only';
type Value = Breakpoint | number;

export interface ResponsiveHelpers {
  isXs: boolean;
  isSm: boolean;
  isMd: boolean;
  isLg: boolean;
  isXl: boolean;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isLargeDesktop: boolean;
  up: (breakpoint: Breakpoint) => boolean;
  down: (breakpoint: Breakpoint) => boolean;
  between: (start: Breakpoint, end: Breakpoint) => boolean;
  only: (breakpoint: Breakpoint) => boolean;
  width: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

export function useResponsive(
  query?: Query,
  start?: Value,
  end?: Value
): ResponsiveHelpers | boolean {
  const theme = useTheme<AppTheme>();

  // Responsive checks for specific breakpoints
  const isXs = useMediaQuery(theme.breakpoints.only('xs'), { noSsr: true });
  const isSm = useMediaQuery(theme.breakpoints.only('sm'), { noSsr: true });
  const isMd = useMediaQuery(theme.breakpoints.only('md'), { noSsr: true });
  const isLg = useMediaQuery(theme.breakpoints.only('lg'), { noSsr: true });
  const isXl = useMediaQuery(theme.breakpoints.only('xl'), { noSsr: true });

  // Device category helpers
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'), {
    noSsr: true,
  });
  const isDesktop = useMediaQuery(theme.breakpoints.between('md', 'xl'), {
    noSsr: true,
  });
  const isLargeDesktop = useMediaQuery(theme.breakpoints.up('xl'), {
    noSsr: true,
  });

  // Determine current width category
  const width = useMemo<'xs' | 'sm' | 'md' | 'lg' | 'xl'>(() => {
    if (isXs) return 'xs';
    if (isSm) return 'sm';
    if (isMd) return 'md';
    if (isLg) return 'lg';
    if (isXl) return 'xl';
    return 'md'; // Default fallback
  }, [isXs, isSm, isMd, isLg, isXl]);

  // Helper functions for up/down logic - memoize to prevent recreation on each render
  const upCheck = useCallback(
    (breakpoint: Breakpoint): boolean => {
      switch (breakpoint) {
        case 'xs':
          return true;
        case 'sm':
          return !isXs;
        case 'md':
          return !isXs && !isSm;
        case 'lg':
          return !isXs && !isSm && !isMd;
        case 'xl':
          return isXl;
        default:
          return false;
      }
    },
    [isXs, isSm, isMd, isXl]
  );

  const downCheck = useCallback(
    (breakpoint: Breakpoint): boolean => {
      switch (breakpoint) {
        case 'xs':
          return isXs;
        case 'sm':
          return isXs || isSm;
        case 'md':
          return isXs || isSm || isMd;
        case 'lg':
          return isXs || isSm || isMd || isLg;
        case 'xl':
          return true;
        default:
          return false;
      }
    },
    [isXs, isSm, isMd, isLg]
  );

  // Create the between function with memoization to prevent recreation
  const betweenCheck = useCallback(
    (start: Breakpoint, end: Breakpoint): boolean => {
      return upCheck(start) && downCheck(end);
    },
    [upCheck, downCheck]
  );

  // Create the only function with memoization
  const onlyCheck = useCallback(
    (breakpoint: Breakpoint): boolean => {
      switch (breakpoint) {
        case 'xs':
          return isXs;
        case 'sm':
          return isSm;
        case 'md':
          return isMd;
        case 'lg':
          return isLg;
        case 'xl':
          return isXl;
        default:
          return false;
      }
    },
    [isXs, isSm, isMd, isLg, isXl]
  );

  // Create the responsive helpers object with memoization
  const responsiveHelpers = useMemo<ResponsiveHelpers>(
    () => ({
      isXs,
      isSm,
      isMd,
      isLg,
      isXl,
      isMobile,
      isTablet,
      isDesktop,
      isLargeDesktop,
      width,
      up: upCheck,
      down: downCheck,
      between: betweenCheck,
      only: onlyCheck,
    }),
    [
      isXs,
      isSm,
      isMd,
      isLg,
      isXl,
      isMobile,
      isTablet,
      isDesktop,
      isLargeDesktop,
      width,
      upCheck,
      downCheck,
      betweenCheck,
      onlyCheck,
    ]
  );

  // Return all helpers when no query is provided
  if (!query) {
    return responsiveHelpers;
  }

  // Single queries for specific breakpoint checks
  if (query === 'up' && start) {
    return upCheck(start as Breakpoint);
  }

  if (query === 'down' && start) {
    return downCheck(start as Breakpoint);
  }

  if (query === 'between' && start && end) {
    return betweenCheck(start as Breakpoint, end as Breakpoint);
  }

  if (query === 'only' && start) {
    return onlyCheck(start as Breakpoint);
  }

  return false;
}

// Legacy convenience hooks for compatibility - memoized for better performance
export function useIsDesktop() {
  const responsive = useResponsive() as ResponsiveHelpers;
  return responsive.isDesktop || responsive.isLargeDesktop;
}

export function useIsTablet() {
  const responsive = useResponsive() as ResponsiveHelpers;
  return responsive.isTablet;
}

export function useIsMobile() {
  const responsive = useResponsive() as ResponsiveHelpers;
  return responsive.isMobile;
}
